import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// ── Design tokens — Iconic ────────────────────────────────────────────────────
const IC = {
  bg:          '#FFFFFF',
  surface:     'var(--bg-subtle)',
  surface2:    'var(--bg)',
  border:      '0.5px solid var(--border)',
  borderMd:    '1px solid var(--border-2)',
  ink1:        'var(--ink)',
  ink2:        'var(--ink-2)',
  ink3:        'var(--ink-3)',
  accent:      'var(--accent)',
  accentSoft:  'var(--accent-dim)',
  accentText:  '#3730A3',
  blue:        'var(--accent)', blueSoft: 'var(--accent-dim)', blueText: 'var(--accent)',
  red:         'var(--red)', redSoft:  'var(--red-dim)', redText:  'var(--red)',
  amber:       'var(--amber)', amberSoft:'var(--amber-dim)', amberText:'var(--amber)',
  green:       'var(--green)', greenSoft:'var(--green-dim)', greenText:'var(--green)',
  rMd:  '8px',
  rLg:  '12px',
  rFull:'9999px',
};

// Semantic color sets
const SEM = {
  blue:  { icon: IC.blue,  soft: IC.blueSoft,  text: IC.blueText },
  red:   { icon: IC.red,   soft: IC.redSoft,   text: IC.redText  },
  amber: { icon: IC.amber, soft: IC.amberSoft, text: IC.amberText},
  green: { icon: IC.green, soft: IC.greenSoft, text: IC.greenText},
  indigo:{ icon: IC.accent,soft: IC.accentSoft,text: IC.accentText},
};

const CAT_LABELS = {
  license: 'Licencias', infrastructure: 'Infraestructura', support: 'Soporte',
  saas: 'SaaS', development: 'Desarrollo', hardware: 'Hardware', other: 'Otros',
};
const CHART_COLORS = ['#6366F1','#3B82F6','#F59E0B','#22C55E','#EF4444','#8B5CF6','#9CA3AF'];

function fmtMoney(n, cur = 'USD') {
  if (!n && n !== 0) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(n);
}
function fmtMoneyDec(n) {
  if (!n && n !== 0) return '—';
  return `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ── Componentes Iconic ────────────────────────────────────────────────────────

/** Icono de Tabler como componente React */
function Ti({ icon, size = 16, style }) {
  return <i className={`ti ti-${icon}`} aria-hidden="true" style={{ fontSize: size, lineHeight: 1, ...style }} />;
}

/** Stat card con icono en contenedor semántico */
function StatCard({ icon, sem = 'blue', label, value, sub, delta, deltaOk }) {
  const c = SEM[sem];
  return (
    <div style={{
      background: IC.bg, border: IC.border, borderRadius: IC.rLg,
      padding: '20px 20px 18px', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Contenedor de icono 34×34 con fondo semántico suave */}
        <div style={{
          width: 34, height: 34, borderRadius: IC.rMd,
          background: c.soft,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Ti icon={icon} size={18} style={{ color: c.icon }} />
        </div>
        {delta != null && (
          <span style={{
            display: 'flex', alignItems: 'center', gap: 3, fontSize: 11,
            color: deltaOk ? IC.green : IC.red, fontWeight: 500,
          }}>
            <Ti icon={deltaOk ? 'trending-up' : 'trending-down'} size={12} />
            {delta}
          </span>
        )}
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 500, color: IC.ink1, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: IC.ink3, marginTop: 4 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: IC.ink3, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

/** Pill de estado — siempre icono + texto + color semántico */
function Pill({ icon, children, sem = 'blue' }) {
  const c = SEM[sem];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px 3px 8px', borderRadius: IC.rFull,
      background: c.soft, color: c.text, fontSize: 12, fontWeight: 500,
      whiteSpace: 'nowrap',
    }}>
      <Ti icon={icon} size={13} />
      {children}
    </span>
  );
}

/** Panel / card */
function Panel({ children, style, noPad }) {
  return (
    <div style={{
      background: IC.bg, border: IC.border, borderRadius: IC.rLg,
      padding: noPad ? 0 : 20, overflow: 'hidden', ...style,
    }}>
      {children}
    </div>
  );
}

/** Encabezado de sección dentro de un panel */
function SectionHead({ icon, children, onToggle, collapsed }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: collapsed ? 0 : 16,
        cursor: onToggle ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && <Ti icon={icon} size={15} style={{ color: IC.ink3 }} />}
        <span style={{ fontSize: 14, fontWeight: 500, color: IC.ink1 }}>{children}</span>
      </div>
      {onToggle && <Ti icon={collapsed ? 'chevron-down' : 'chevron-up'} size={14} style={{ color: IC.ink3 }} />}
    </div>
  );
}

/** Barra de progreso */
function Bar({ pct, color = IC.accent, height = 6 }) {
  return (
    <div style={{ height, background: IC.surface2, borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(100, pct || 0)}%`, background: color, borderRadius: 3 }} />
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function ManagerDashboardDesktop() {
  const isMobile = useIsMobile();
  const { user }  = useAuth();
  const token     = localStorage.getItem('token');
  const headers   = { Authorization: `Bearer ${token}` };

  const [costs, setCosts]         = useState(null);
  const [dep, setDep]             = useState(null);
  const [licenses, setLicenses]   = useState(null);
  const [assets, setAssets]       = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [cloud, setCloud]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [collapsed, setCollapsed] = useState({ costos: false, incidentes: false, depreciacion: false, licencias: false });
  const toggle = k => setCollapsed(p => ({ ...p, [k]: !p[k] }));

  const load = async () => {
    setLoading(true);
    try {
      const [c, d, l, a, i, cl] = await Promise.all([
        fetch(`${API_URL}/costs/summary`,              { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/costs/depreciation-summary`, { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/licenses/summary`,           { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/assets`,                     { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/incidents`,                  { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/cloud-providers/summary`,    { headers }).then(r => r.json()).catch(() => null),
      ]);
      setCosts(c); setDep(d); setLicenses(l);
      setAssets(Array.isArray(a) ? a : []);
      setIncidents(Array.isArray(i) ? i : []);
      setCloud(cl);
      setLastUpdate(new Date());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // ── Métricas ───────────────────────────────────────────────────────────────
  const assetMonthlyCosts = costs?.by_category?.reduce((s, c) => s + parseFloat(c.monthly_total || 0), 0) || 0;
  const licMonthly   = parseFloat(licenses?.monthly_cost || 0);
  const cloudTotal   = parseFloat(cloud?.total || 0);
  const grandMonthly = assetMonthlyCosts + licMonthly + cloudTotal;
  const grandAnnual  = grandMonthly * 12;

  const openIncidents  = incidents.filter(i => !['resuelto','cerrado'].includes(i.status)).length;
  const resolvedLast30 = incidents.filter(i => i.resolved_at && new Date(i.resolved_at) >= new Date(Date.now() - 30*24*60*60*1000)).length;
  const scoredAssets   = assets.filter(a => a.security_score != null);
  const avgSecScore    = scoredAssets.length > 0
    ? Math.round(scoredAssets.reduce((s, a) => s + (a.security_score || 0), 0) / scoredAssets.length) : null;
  const activeAssets = assets.filter(a => a.status === 'active' || a.status === 'warning').length;

  const catData = (costs?.by_category || []).map((c, i) => ({
    name: CAT_LABELS[c.category] || c.category,
    value: Math.round(parseFloat(c.monthly_total || 0)),
    color: CHART_COLORS[i % CHART_COLORS.length],
  })).filter(c => c.value > 0);

  const incByPriority = ['critica','alta','media','baja'].map(p => ({
    label: { critica: 'Crítica', alta: 'Alta', media: 'Media', baja: 'Baja' }[p],
    count: incidents.filter(i => i.priority === p && !['resuelto','cerrado'].includes(i.status)).length,
    sem:   p === 'critica' ? 'red' : p === 'alta' ? 'amber' : p === 'baja' ? 'blue' : 'indigo',
    color: p === 'critica' ? IC.red : p === 'alta' ? IC.amber : p === 'media' ? IC.accent : IC.ink3,
  }));

  const osMap = {};
  assets.forEach(a => {
    if (!a.os_name) return;
    const os = a.os_name.toLowerCase();
    let cat = 'Otros';
    if (os.includes('windows')) cat = 'Windows';
    else if (['linux','ubuntu','debian','centos','rhel','fedora','alma','rocky'].some(x => os.includes(x))) cat = 'Linux';
    else if (os.includes('mac') || os.includes('darwin')) cat = 'macOS';
    osMap[cat] = (osMap[cat] || 0) + 1;
  });
  const osData = Object.entries(osMap).map(([name, value], i) => ({ name, value, color: CHART_COLORS[i] }));

  const today = new Date();

  const ttStyle = { background: IC.bg, border: IC.borderMd, borderRadius: IC.rMd, fontSize: 11 };

  const providerIcon = { aws: 'brand-aws', gcp: 'brand-google', azure: 'brand-azure', office365: 'brand-office', digitalocean: 'brand-digitalocean', custom: 'cloud' };
  const providerLabel = { aws: 'AWS', gcp: 'Google Cloud', azure: 'Azure', office365: 'Microsoft 365', digitalocean: 'DigitalOcean', custom: 'Cloud' };

  const cols = isMobile ? '1fr' : 'repeat(2, 1fr)';
  const gap  = 14;

  return (
    <div style={{ background: IC.surface, minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Tabler Icons CDN */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&display=swap');
        @import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css');
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '16px 12px 40px' : '24px 24px 40px' }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 24, flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <Ti icon="building" size={18} style={{ color: IC.ink3 }} />
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: IC.ink1 }}>Panel Gerencial</h1>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: IC.ink3 }}>
              {user?.full_name} · Actualizado {lastUpdate.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
          <button onClick={load} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', background: IC.bg, border: IC.borderMd,
            borderRadius: IC.rMd, fontSize: 13, fontWeight: 500, color: IC.ink2, cursor: 'pointer',
          }}>
            <Ti icon="refresh" size={14} style={{ opacity: loading ? 0.3 : 1 }} />
            Actualizar
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div className="spinner" style={{ margin: '0 auto', width: 32, height: 32, borderTopColor: IC.accent }} />
            <p style={{ color: IC.ink3, marginTop: 12, fontSize: 13 }}>Cargando datos...</p>
          </div>
        ) : (<>

          {/* ── Fila 1: Stat cards principales ── */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap, marginBottom: gap }}>
            <StatCard
              icon="currency-dollar" sem="indigo"
              label="Costo mensual total"
              value={fmtMoney(grandMonthly)}
              sub={`${fmtMoney(grandAnnual)} / año`}
            />
            <StatCard
              icon={openIncidents > 5 ? 'alert-triangle' : 'ticket'}
              sem={openIncidents > 10 ? 'red' : openIncidents > 5 ? 'amber' : 'green'}
              label="Incidentes abiertos"
              value={openIncidents}
              sub={`${resolvedLast30} resueltos · 30d`}
            />
            <StatCard
              icon="shield"
              sem={avgSecScore == null ? 'blue' : avgSecScore >= 80 ? 'green' : avgSecScore >= 60 ? 'amber' : 'red'}
              label="Score de seguridad"
              value={avgSecScore != null ? `${avgSecScore}/100` : '—'}
              sub={`${scoredAssets.length} equipos evaluados`}
            />
            <StatCard
              icon="devices" sem="blue"
              label="Activos en inventario"
              value={activeAssets}
              sub={`${assets.length} total registrados`}
            />
          </div>

          {/* ── Fila 1b: Desglose de costos por fuente ── */}
          {(assetMonthlyCosts > 0 || licMonthly > 0 || cloudTotal > 0) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : `repeat(${[assetMonthlyCosts, licMonthly, cloudTotal].filter(v => v > 0).length}, 1fr)`,
              gap, marginBottom: gap,
            }}>
              {assetMonthlyCosts > 0 && (
                <Panel style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: IC.rMd, background: SEM.blue.soft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ti icon="server" size={17} style={{ color: SEM.blue.icon }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: IC.ink3 }}>Operativo activos</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: IC.ink1 }}>{fmtMoney(assetMonthlyCosts)}</div>
                  </div>
                </Panel>
              )}
              {licMonthly > 0 && (
                <Panel style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: IC.rMd, background: SEM.indigo.soft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ti icon="key" size={17} style={{ color: SEM.indigo.icon }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: IC.ink3 }}>Licencias de software</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: IC.ink1 }}>{fmtMoney(licMonthly)}</div>
                  </div>
                </Panel>
              )}
              {cloudTotal > 0 && (
                <Panel style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: IC.rMd, background: SEM.amber.soft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ti icon="cloud" size={17} style={{ color: SEM.amber.icon }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: IC.ink3 }}>Infraestructura cloud</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: IC.ink1 }}>{fmtMoneyDec(cloudTotal)} USD</div>
                  </div>
                </Panel>
              )}
            </div>
          )}

          {/* ── Costos cloud por proveedor ── */}
          {cloud?.providers?.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : `repeat(${Math.min(cloud.providers.length, 4)}, 1fr)`,
              gap, marginBottom: gap,
            }}>
              {cloud.providers.map(p => (
                <Panel key={p.id} style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: IC.rMd, background: IC.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Ti icon={providerIcon[p.provider] || 'cloud'} size={18} style={{ color: IC.ink2 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: IC.ink1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {providerLabel[p.provider] || p.name}
                    </div>
                    <div style={{ fontSize: 11, color: IC.ink3 }}>{p.status === 'connected' ? 'Conectado' : 'Manual'}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: IC.ink1, flexShrink: 0 }}>
                    {p.cost > 0 ? `$${Number(p.cost).toFixed(2)}` : '—'}
                  </div>
                </Panel>
              ))}
            </div>
          )}

          {/* ── Costos + Incidentes ── */}
          <div style={{ display: 'grid', gridTemplateColumns: cols, gap, marginBottom: gap }}>

            <Panel>
              <SectionHead icon="chart-bar" onToggle={() => toggle('costos')} collapsed={collapsed.costos}>
                Costos operativos · mensual
              </SectionHead>
              {!collapsed.costos && (catData.length === 0 ? (
                <p style={{ fontSize: 13, color: IC.ink3, textAlign: 'center', padding: '32px 0', margin: 0 }}>Sin costos registrados</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'center' }}>
                  <div style={{ flexShrink: 0, width: isMobile ? '100%' : 150, height: 150 }}>
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie data={catData} cx="50%" cy="50%" innerRadius={42} outerRadius={65} dataKey="value" paddingAngle={2}>
                          {catData.map((c, i) => <Cell key={i} fill={c.color} />)}
                        </Pie>
                        <Tooltip contentStyle={ttStyle} formatter={v => [`$${v.toLocaleString('es-AR')}`, 'Mensual']} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {catData.map((c, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `0.5px solid ${IC.surface2}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: IC.ink2 }}>{c.name}</span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 500, color: IC.ink1 }}>${c.value.toLocaleString('es-AR')}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontSize: 12, fontWeight: 600, color: IC.ink1 }}>
                      <span>Total</span><span>${Math.round(assetMonthlyCosts).toLocaleString('es-AR')}/mes</span>
                    </div>
                  </div>
                </div>
              ))}
            </Panel>

            <Panel>
              <SectionHead icon="alert-triangle" onToggle={() => toggle('incidentes')} collapsed={collapsed.incidentes}>
                Incidentes abiertos · por prioridad
              </SectionHead>
              {!collapsed.incidentes && (incByPriority.every(p => p.count === 0) ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Pill icon="circle-check" sem="green">Sin incidentes abiertos</Pill>
                </div>
              ) : (
                <div>
                  {incByPriority.map((p, i) => (
                    <div key={i} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: p.count > 0 ? IC.ink1 : IC.ink3 }}>{p.label}</span>
                        <span style={{ fontSize: 20, fontWeight: 500, color: p.count > 0 ? p.color : IC.ink3, lineHeight: 1 }}>{p.count}</span>
                      </div>
                      <Bar pct={openIncidents > 0 ? (p.count / openIncidents) * 100 : 0} color={p.color} />
                    </div>
                  ))}
                  <div style={{ marginTop: 12, fontSize: 12, color: IC.ink3 }}>
                    <Ti icon="check" size={12} style={{ marginRight: 4, color: IC.green }} />
                    {resolvedLast30} resueltos en los últimos 30 días
                  </div>
                </div>
              ))}
            </Panel>
          </div>

          {/* ── Depreciación + Licencias ── */}
          <div style={{ display: 'grid', gridTemplateColumns: cols, gap, marginBottom: gap }}>

            <Panel>
              <SectionHead icon="trending-down" onToggle={() => toggle('depreciacion')} collapsed={collapsed.depreciacion}>
                Depreciación de activos
              </SectionHead>
              {!collapsed.depreciacion && (<>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Valor de compra',       value: fmtMoney(dep?.totalPurchaseValue), icon: 'tag' },
                    { label: 'Valor actual (libro)',   value: fmtMoney(dep?.totalCurrentValue),  icon: 'coin' },
                    { label: 'Depreciado acumulado',   value: fmtMoney(dep?.totalAccumulated),   icon: 'trending-down' },
                    { label: 'Tot. depreciados',       value: dep?.fullyDepreciated || 0,         icon: 'archive' },
                  ].map((m, i) => (
                    <div key={i} style={{ background: IC.surface, borderRadius: IC.rMd, padding: '12px 14px' }}>
                      <div style={{ fontSize: 10, color: IC.ink3, fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: IC.ink1 }}>{m.value}</div>
                    </div>
                  ))}
                </div>
                {dep?.assets?.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: IC.surface }}>
                        {['Equipo','Valor actual','Dep. %'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Equipo' ? 'left' : 'right', fontSize: 11, fontWeight: 500, color: IC.ink3, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `0.5px solid ${IC.surface2}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dep.assets.slice(0, 5).map((a, i) => (
                        <tr key={i} style={{ borderBottom: `0.5px solid ${IC.surface2}` }}>
                          <td style={{ padding: '8px 10px', color: IC.ink1, fontWeight: 500 }}>
                            {a.hostname}
                            {a.fully_depreciated && (
                              <Pill icon="alert-circle" sem="red" style={{ marginLeft: 6 }}>100%</Pill>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: IC.ink2 }}>
                            {fmtMoney(a.current_value, a.currency)}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500, color: a.pct_depreciated >= 80 ? IC.red : IC.ink2 }}>
                            {a.pct_depreciated}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ fontSize: 12, color: IC.ink3, textAlign: 'center', padding: '20px 0', margin: 0 }}>Sin compras registradas</p>
                )}
              </>)}
            </Panel>

            <Panel>
              <SectionHead icon="key" onToggle={() => toggle('licencias')} collapsed={collapsed.licencias}>
                Estado de licencias
              </SectionHead>
              {!collapsed.licencias && (<>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Licencias activas', value: licenses?.total_licenses || 0 },
                    { label: 'Costo mensual',     value: fmtMoney(licMonthly) },
                    { label: 'Seats utilizados',  value: `${licenses?.used_seats || 0}/${licenses?.total_seats || 0}` },
                    { label: 'Por vencer (60d)',   value: licenses?.expiring_soon?.length || 0 },
                  ].map((m, i) => (
                    <div key={i} style={{ background: IC.surface, borderRadius: IC.rMd, padding: '12px 14px' }}>
                      <div style={{ fontSize: 10, color: IC.ink3, fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: IC.ink1 }}>{m.value}</div>
                    </div>
                  ))}
                </div>

                {(licenses?.over_assigned?.length > 0 || licenses?.expiring_soon?.length > 0) ? (
                  <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {licenses.over_assigned?.map((l, i) => (
                      <div key={`o-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: IC.redSoft, borderRadius: IC.rMd }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: IC.ink1 }}>{l.software_name}</span>
                        <Pill icon="alert-triangle" sem="red">{l.used_seats}/{l.total_seats} seats</Pill>
                      </div>
                    ))}
                    {licenses.expiring_soon?.map((l, i) => (
                      <div key={`e-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: IC.amberSoft, borderRadius: IC.rMd }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: IC.ink1 }}>{l.software_name}</span>
                        <Pill icon="clock" sem="amber">{new Date(l.expiry_date).toLocaleDateString('es-AR')}</Pill>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ marginBottom: 14, textAlign: 'center' }}>
                    <Pill icon="circle-check" sem="green">Sin alertas de licencias</Pill>
                  </div>
                )}

                {(licenses?.total_seats || 0) > 0 && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: IC.ink3, marginBottom: 6 }}>
                      <span>Ocupación global de seats</span>
                      <span style={{ fontWeight: 500 }}>{Math.round(((licenses?.used_seats || 0) / (licenses?.total_seats || 1)) * 100)}%</span>
                    </div>
                    <Bar pct={Math.round(((licenses?.used_seats || 0) / (licenses?.total_seats || 1)) * 100)} />
                  </div>
                )}
              </>)}
            </Panel>
          </div>

          {/* ── SO + Seguridad ── */}
          <div style={{ display: 'grid', gridTemplateColumns: cols, gap, marginBottom: gap }}>

            <Panel>
              <SectionHead icon="devices">Distribución de sistemas operativos</SectionHead>
              {osData.length === 0 ? (
                <p style={{ fontSize: 12, color: IC.ink3, textAlign: 'center', padding: '32px 0', margin: 0 }}>Sin datos de SO</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'center' }}>
                  <div style={{ flexShrink: 0, width: isMobile ? '100%' : 150, height: 150 }}>
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie data={osData} cx="50%" cy="50%" innerRadius={42} outerRadius={65} dataKey="value" paddingAngle={2}>
                          {osData.map((o, i) => <Cell key={i} fill={o.color} />)}
                        </Pie>
                        <Tooltip contentStyle={ttStyle} formatter={(v, n) => [v, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ flex: 1 }}>
                    {osData.map((o, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `0.5px solid ${IC.surface2}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: o.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: IC.ink2 }}>{o.name}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 500, color: IC.ink1 }}>{o.value}</span>
                          <span style={{ fontSize: 11, color: IC.ink3 }}>({Math.round((o.value / assets.length) * 100)}%)</span>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, fontSize: 12, fontWeight: 500, color: IC.ink1 }}>
                      <span>Total</span><span>{assets.length} activos</span>
                    </div>
                  </div>
                </div>
              )}
            </Panel>

            <Panel>
              <SectionHead icon="shield">Calificación de seguridad</SectionHead>
              {scoredAssets.length === 0 ? (
                <p style={{ fontSize: 12, color: IC.ink3, textAlign: 'center', padding: '32px 0', margin: 0 }}>Sin activos evaluados</p>
              ) : (
                <div>
                  {[
                    { label: 'Seguro',             icon: 'circle-check', sem: 'green', count: assets.filter(a => (a.security_score ?? -1) >= 80).length },
                    { label: 'Aceptable',           icon: 'shield-check', sem: 'blue',  count: assets.filter(a => (a.security_score ?? -1) >= 60 && (a.security_score ?? -1) < 80).length },
                    { label: 'Atención requerida',  icon: 'alert-circle', sem: 'amber', count: assets.filter(a => (a.security_score ?? -1) >= 40 && (a.security_score ?? -1) < 60).length },
                    { label: 'Crítico',             icon: 'alert-triangle', sem: 'red', count: assets.filter(a => a.security_score != null && a.security_score < 40).length },
                  ].map((g, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: IC.surface, borderRadius: IC.rMd, marginBottom: 8 }}>
                      <Pill icon={g.icon} sem={g.sem}>{g.label}</Pill>
                      <span style={{ fontSize: 20, fontWeight: 500, color: IC.ink1 }}>{g.count}</span>
                    </div>
                  ))}
                  {avgSecScore != null && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: IC.ink3, marginBottom: 6 }}>
                        <span>Score promedio</span><span style={{ fontWeight: 500, color: IC.ink1 }}>{avgSecScore}/100</span>
                      </div>
                      <Bar pct={avgSecScore} color={avgSecScore >= 80 ? IC.green : avgSecScore >= 60 ? IC.amber : IC.red} />
                    </div>
                  )}
                </div>
              )}
            </Panel>
          </div>

          {/* ── Top activos + Vencimientos ── */}
          <div style={{ display: 'grid', gridTemplateColumns: cols, gap }}>

            <Panel>
              <SectionHead icon="chart-bar">Top activos · costo mensual</SectionHead>
              {!costs?.by_asset?.length ? (
                <p style={{ fontSize: 12, color: IC.ink3, textAlign: 'center', padding: '32px 0', margin: 0 }}>Sin datos de costos por activo</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: IC.surface }}>
                      {['Activo', 'Tipo', 'Mensual'].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Mensual' ? 'right' : 'left', fontSize: 11, fontWeight: 500, color: IC.ink3, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `0.5px solid ${IC.surface2}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {costs.by_asset.map((a, i) => (
                      <tr key={i} style={{ borderBottom: `0.5px solid ${IC.surface2}` }}>
                        <td style={{ padding: '9px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: IC.rMd, background: IC.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Ti icon={a.asset_type === 'server' ? 'server' : a.asset_type === 'laptop' ? 'device-laptop' : 'desktop'} size={14} style={{ color: IC.ink3 }} />
                            </div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 500, color: IC.ink1 }}>{a.hostname}</div>
                              {a.location_name && <div style={{ fontSize: 11, color: IC.ink3 }}>{a.location_name}</div>}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: 11, color: IC.ink3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {a.asset_type}
                        </td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 500, color: IC.ink1 }}>
                          {fmtMoney(parseFloat(a.monthly_total || 0), a.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel>
              <SectionHead icon="clock">Próximos vencimientos · 60 días</SectionHead>
              {!costs?.upcoming_renewals?.length ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Pill icon="circle-check" sem="green">Sin vencimientos próximos</Pill>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {costs.upcoming_renewals.map((r, i) => {
                    const days   = Math.round((new Date(r.due_date) - today) / (24*60*60*1000));
                    const urgent = days <= 15;
                    return (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 12px',
                        background: urgent ? IC.redSoft : IC.amberSoft,
                        borderRadius: IC.rMd,
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: IC.ink1 }}>{r.description}</div>
                          <div style={{ fontSize: 11, color: IC.ink3, fontFamily: "'IBM Plex Mono', monospace" }}>
                            {r.hostname} · {r.vendor || '—'}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0, marginLeft: 12, textAlign: 'right' }}>
                          <Pill icon={urgent ? 'alert-triangle' : 'clock'} sem={urgent ? 'red' : 'amber'}>
                            {days === 0 ? 'Hoy' : `${days}d`}
                          </Pill>
                          <div style={{ fontSize: 10, color: IC.ink3, marginTop: 4 }}>
                            {new Date(r.due_date).toLocaleDateString('es-AR')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </div>

        </>)}
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  return <ManagerDashboardDesktop />;
}
